/**
 * Best-effort Unicode → LaTeX encoder.
 *
 * Takes paste-anywhere Unicode math (Greek glyphs, ⁄ fractions, ⁰¹²³ super/subscripts,
 * ℝ ℕ ℂ blackboard letters, etc.) and emits a LaTeX source string that KaTeX can render.
 * This is the inverse of `latexDecode.js` — together they let the Equation Builder show
 * two live previews (one for each flavor) regardless of which mode the workspace is in.
 *
 * Conservative: if a char already looks like a LaTeX command or has no mapping, it's
 * passed through unchanged.
 */

// Greek glyph → \command (lowercase + uppercase). Variant forms (ϵ, ϑ, ϕ, ϱ, ϖ) also covered.
const GREEK_TO_TEX = {
  "α":"\\alpha","β":"\\beta","γ":"\\gamma","δ":"\\delta","ε":"\\varepsilon","ϵ":"\\epsilon",
  "ζ":"\\zeta","η":"\\eta","θ":"\\theta","ϑ":"\\vartheta","ι":"\\iota","κ":"\\kappa","ϰ":"\\varkappa",
  "λ":"\\lambda","μ":"\\mu","ν":"\\nu","ξ":"\\xi","ο":"o","π":"\\pi","ϖ":"\\varpi",
  "ρ":"\\rho","ϱ":"\\varrho","σ":"\\sigma","ς":"\\varsigma","τ":"\\tau","υ":"\\upsilon",
  "φ":"\\varphi","ϕ":"\\phi","χ":"\\chi","ψ":"\\psi","ω":"\\omega",
  "Α":"A","Β":"B","Γ":"\\Gamma","Δ":"\\Delta","Ε":"E","Ζ":"Z","Η":"H","Θ":"\\Theta",
  "Ι":"I","Κ":"K","Λ":"\\Lambda","Μ":"M","Ν":"N","Ξ":"\\Xi","Ο":"O","Π":"\\Pi",
  "Ρ":"P","Σ":"\\Sigma","Τ":"T","Υ":"\\Upsilon","Φ":"\\Phi","Χ":"X","Ψ":"\\Psi","Ω":"\\Omega",
};

// Common math operators / relations / arrows.
const SYM_TO_TEX = {
  "∂":"\\partial","∇":"\\nabla","∞":"\\infty","ℵ":"\\aleph","ℏ":"\\hbar","ℓ":"\\ell",
  "∑":"\\sum","∏":"\\prod","∐":"\\coprod","∫":"\\int","∬":"\\iint","∭":"\\iiint","∮":"\\oint","∯":"\\oiint",
  "≤":"\\le","≥":"\\ge","≠":"\\ne","≡":"\\equiv","≈":"\\approx","∼":"\\sim","≃":"\\simeq","≅":"\\cong","∝":"\\propto",
  "≪":"\\ll","≫":"\\gg","≺":"\\prec","≻":"\\succ","≼":"\\preceq","≽":"\\succeq",
  "∈":"\\in","∉":"\\notin","∋":"\\ni","⊂":"\\subset","⊃":"\\supset","⊆":"\\subseteq","⊇":"\\supseteq",
  "∪":"\\cup","∩":"\\cap","∖":"\\setminus","∅":"\\emptyset","∀":"\\forall","∃":"\\exists","∄":"\\nexists",
  "∧":"\\land","∨":"\\lor","¬":"\\neg","⊕":"\\oplus","⊗":"\\otimes","⊖":"\\ominus","⊙":"\\odot",
  "·":"\\cdot","×":"\\times","÷":"\\div","∗":"\\ast","⋆":"\\star","∘":"\\circ","±":"\\pm","∓":"\\mp",
  "→":"\\to","←":"\\leftarrow","↔":"\\leftrightarrow","⇒":"\\Rightarrow","⇐":"\\Leftarrow","⇔":"\\Leftrightarrow",
  "↦":"\\mapsto","⟶":"\\longrightarrow","⟵":"\\longleftarrow","↑":"\\uparrow","↓":"\\downarrow",
  "∴":"\\therefore","∵":"\\because","∠":"\\angle","⊥":"\\perp","∥":"\\parallel",
  "□":"\\Box","◇":"\\diamond","△":"\\triangle","√":"\\surd","…":"\\ldots","⋯":"\\cdots","⋮":"\\vdots","⋱":"\\ddots",
  "ℜ":"\\Re","ℑ":"\\Im","℘":"\\wp","℧":"\\mho",
  // Number sets — single-glyph Unicode → mathbb
  "ℕ":"\\mathbb{N}","ℤ":"\\mathbb{Z}","ℚ":"\\mathbb{Q}","ℝ":"\\mathbb{R}","ℂ":"\\mathbb{C}",
  "ℍ":"\\mathbb{H}","ℙ":"\\mathbb{P}","𝔼":"\\mathbb{E}","𝔽":"\\mathbb{F}","𝕊":"\\mathbb{S}",
};

// Unicode superscript / subscript glyph → its plain ASCII source character.
// Used to detect runs and emit `^{...}` / `_{...}` blocks for KaTeX.
const SUP_REV = {
  "⁰":"0","¹":"1","²":"2","³":"3","⁴":"4","⁵":"5","⁶":"6","⁷":"7","⁸":"8","⁹":"9",
  "⁺":"+","⁻":"-","⁼":"=","⁽":"(","⁾":")",
  "ᵃ":"a","ᵇ":"b","ᶜ":"c","ᵈ":"d","ᵉ":"e","ᶠ":"f","ᵍ":"g","ʰ":"h","ⁱ":"i","ʲ":"j",
  "ᵏ":"k","ˡ":"l","ᵐ":"m","ⁿ":"n","ᵒ":"o","ᵖ":"p","ʳ":"r","ˢ":"s","ᵗ":"t","ᵘ":"u",
  "ᵛ":"v","ʷ":"w","ˣ":"x","ʸ":"y","ᶻ":"z",
  // Greek superscript glyphs → \alpha \beta etc.
  "ᵅ":"\\alpha","ᵝ":"\\beta","ᵞ":"\\gamma","ᵟ":"\\delta","ᵋ":"\\varepsilon",
  "ᶿ":"\\theta","ᶥ":"\\iota","ᵠ":"\\phi","ᵡ":"\\chi",
};
const SUB_REV = {
  "₀":"0","₁":"1","₂":"2","₃":"3","₄":"4","₅":"5","₆":"6","₇":"7","₈":"8","₉":"9",
  "₊":"+","₋":"-","₌":"=","₍":"(","₎":")",
  "ₐ":"a","ₑ":"e","ₕ":"h","ᵢ":"i","ⱼ":"j","ₖ":"k","ₗ":"l","ₘ":"m","ₙ":"n","ₒ":"o",
  "ₚ":"p","ᵣ":"r","ₛ":"s","ₜ":"t","ᵤ":"u","ᵥ":"v","ₓ":"x",
  // Greek subscript glyphs → \beta \gamma \rho \phi \chi
  "ᵦ":"\\beta","ᵧ":"\\gamma","ᵨ":"\\rho","ᵩ":"\\phi","ᵪ":"\\chi",
};

// Blackboard / calligraphic / fraktur single-glyph → mathbb / mathcal / mathfrak LaTeX
const MATHBB_REV = {
  "𝔸":"A","𝔹":"B","ℂ":"C","𝔻":"D","𝔼":"E","𝔽":"F","𝔾":"G","ℍ":"H","𝕀":"I","𝕁":"J","𝕂":"K","𝕃":"L","𝕄":"M","ℕ":"N",
  "𝕆":"O","ℙ":"P","ℚ":"Q","ℝ":"R","𝕊":"S","𝕋":"T","𝕌":"U","𝕍":"V","𝕎":"W","𝕏":"X","𝕐":"Y","ℤ":"Z",
  "𝕒":"a","𝕓":"b","𝕔":"c","𝕕":"d","𝕖":"e","𝕗":"f","𝕘":"g","𝕙":"h","𝕚":"i","𝕛":"j","𝕜":"k","𝕝":"l","𝕞":"m","𝕟":"n",
  "𝕠":"o","𝕡":"p","𝕢":"q","𝕣":"r","𝕤":"s","𝕥":"t","𝕦":"u","𝕧":"v","𝕨":"w","𝕩":"x","𝕪":"y","𝕫":"z",
  "𝟘":"0","𝟙":"1","𝟚":"2","𝟛":"3","𝟜":"4","𝟝":"5","𝟞":"6","𝟟":"7","𝟠":"8","𝟡":"9",
};
const MATHCAL_REV = {
  "𝒜":"A","ℬ":"B","𝒞":"C","𝒟":"D","ℰ":"E","ℱ":"F","𝒢":"G","ℋ":"H","ℐ":"I","𝒥":"J",
  "𝒦":"K","ℒ":"L","ℳ":"M","𝒩":"N","𝒪":"O","𝒫":"P","𝒬":"Q","ℛ":"R","𝒮":"S","𝒯":"T",
  "𝒰":"U","𝒱":"V","𝒲":"W","𝒳":"X","𝒴":"Y","𝒵":"Z",
  "𝒶":"a","𝒷":"b","𝒸":"c","𝒹":"d","ℯ":"e","𝒻":"f","ℊ":"g","𝒽":"h","𝒾":"i","𝒿":"j",
  "𝓀":"k","ℓ":"l","𝓂":"m","𝓃":"n","ℴ":"o","𝓅":"p","𝓆":"q","𝓇":"r","𝓈":"s","𝓉":"t",
  "𝓊":"u","𝓋":"v","𝓌":"w","𝓍":"x","𝓎":"y","𝓏":"z",
};
const MATHFRAK_REV = {
  "𝔄":"A","𝔅":"B","ℭ":"C","𝔇":"D","𝔈":"E","𝔉":"F","𝔊":"G","ℌ":"H","ℑ":"I","𝔍":"J",
  "𝔎":"K","𝔏":"L","𝔐":"M","𝔑":"N","𝔒":"O","𝔓":"P","𝔔":"Q","ℜ":"R","𝔖":"S","𝔗":"T",
  "𝔘":"U","𝔙":"V","𝔚":"W","𝔛":"X","𝔜":"Y","ℨ":"Z",
  "𝔞":"a","𝔟":"b","𝔠":"c","𝔡":"d","𝔢":"e","𝔣":"f","𝔤":"g","𝔥":"h","𝔦":"i","𝔧":"j",
  "𝔨":"k","𝔩":"l","𝔪":"m","𝔫":"n","𝔬":"o","𝔭":"p","𝔮":"q","𝔯":"r","𝔰":"s","𝔱":"t",
  "𝔲":"u","𝔳":"v","𝔴":"w","𝔵":"x","𝔶":"y","𝔷":"z",
};

// Pre-baked Unicode vulgar fractions → \dfrac{a}{b}
const VULGAR_FRAC = {
  "½":"\\dfrac{1}{2}","⅓":"\\dfrac{1}{3}","¼":"\\dfrac{1}{4}","⅕":"\\dfrac{1}{5}","⅙":"\\dfrac{1}{6}",
  "⅐":"\\dfrac{1}{7}","⅛":"\\dfrac{1}{8}","⅑":"\\dfrac{1}{9}","⅒":"\\dfrac{1}{10}",
  "⅔":"\\dfrac{2}{3}","¾":"\\dfrac{3}{4}","⅖":"\\dfrac{2}{5}","⅗":"\\dfrac{3}{5}","⅘":"\\dfrac{4}{5}",
  "⅚":"\\dfrac{5}{6}","⅜":"\\dfrac{3}{8}","⅝":"\\dfrac{5}{8}","⅞":"\\dfrac{7}{8}","↉":"\\dfrac{0}{3}",
};

const isWordBreak = (c) => !c || /[\s+\-=()\[\]{}<>,;·×÷*/]/.test(c);

/**
 * Convert paste-anywhere Unicode math text into a LaTeX source string.
 *
 *   - U+2044 FRACTION SLASH `X⁄Y` → `\dfrac{X}{Y}` (numerator = run of non-break chars
 *     before slash, denominator = run after).
 *   - Vulgar fractions ½ ⅔ ¾ etc → `\dfrac{a}{b}`.
 *   - Superscript / subscript runs (²ⁿ⁺¹, ᵢⱼ) → `^{2n+1}` / `_{ij}` (recursively).
 *   - Greek and operator glyphs → corresponding LaTeX command.
 *   - Blackboard / script / fraktur letters → `\mathbb{}` / `\mathcal{}` / `\mathfrak{}`.
 *   - Anything else (digits, plain Latin letters, existing LaTeX with backslashes) passes through.
 */
export function unicodeToLatex(input) {
  if (!input) return "";
  // Strip Builder's empty-slot marker — KaTeX would error on it.
  const src = input.replace(/■/g, "\\square ");
  let out = "";
  let i = 0;

  while (i < src.length) {
    const c = src[i];

    // Pass LaTeX commands straight through (so `\dfrac{a}{b}` etc don't get re-encoded).
    if (c === "\\") {
      let k = i + 1;
      while (k < src.length && /[a-zA-Z]/.test(src[k])) k++;
      out += src.slice(i, k);
      i = k;
      continue;
    }

    // U+2044 FRACTION SLASH — build a \dfrac. Numerator = preceding non-break run that
    // hasn't already been emitted; denominator = following non-break run.
    if (c === "\u2044") {
      // Walk back through `out` (which still has the un-encoded source for the numerator
      // since we just passed it through) to grab the last non-break run.
      // Simpler: walk src backward from i-1 looking for the start of the numerator,
      // re-encode it, drop it from `out`, emit \dfrac{...}{...}.
      let nStart = i;
      while (nStart > 0 && !isWordBreak(src[nStart - 1])) nStart--;
      let dEnd = i + 1;
      while (dEnd < src.length && !isWordBreak(src[dEnd])) dEnd++;
      const numRaw = src.slice(nStart, i);
      const denRaw = src.slice(i + 1, dEnd);
      if (numRaw && denRaw) {
        // Remove the numerator we already pushed onto `out` (it's the last len(numRaw) chars,
        // but those may have been re-encoded — easier path: recurse with raw text).
        const numEnc = unicodeToLatex(numRaw);
        const denEnc = unicodeToLatex(denRaw);
        // Strip the trailing un-encoded numerator from `out` — count how much of `out`
        // we pushed since position nStart. Since we re-encoded as we went, we can't trim
        // by char count. Workaround: re-encode the whole numerator now and replace last N
        // chars of out by recomputing.
        // Easier approach: restart from nStart for a clean re-encoding.
        // To keep this simple we'll just slice out the last encoded numerator length:
        const trimLen = numEnc.length;
        if (out.endsWith(numEnc)) out = out.slice(0, out.length - trimLen);
        out += `\\dfrac{${numEnc}}{${denEnc}}`;
        i = dEnd;
        continue;
      }
      // Empty side — just emit a regular slash and move on
      out += "/";
      i++;
      continue;
    }

    // Vulgar fraction single glyph
    if (Object.prototype.hasOwnProperty.call(VULGAR_FRAC, c)) {
      out += VULGAR_FRAC[c];
      i++;
      continue;
    }

    // Paren-wrapped superscript ⁽…⁾ — index group that may contain non-superscriptable
    // chars (Greek letters, etc.). Decode the interior char-by-char via SUP_REV (NOT a
    // recursive `unicodeToLatex` call — that would double-wrap into `^{^{…}}` because
    // the recursion treats the run AS a superscript and emits its own `^{…}`).
    if (c === "⁽") {
      const close = src.indexOf("⁾", i + 1);
      if (close > -1) {
        let run = "";
        for (let j = i + 1; j < close; j++) {
          const ch = src[j];
          const tok = Object.prototype.hasOwnProperty.call(SUP_REV, ch) ? SUP_REV[ch] : ch;
          run += tok.startsWith("\\") ? tok + " " : tok;
        }
        out += `^{${run.trimEnd()}}`;
        i = close + 1;
        continue;
      }
    }
    if (c === "₍") {
      const close = src.indexOf("₎", i + 1);
      if (close > -1) {
        let run = "";
        for (let j = i + 1; j < close; j++) {
          const ch = src[j];
          const tok = Object.prototype.hasOwnProperty.call(SUB_REV, ch) ? SUB_REV[ch] : ch;
          run += tok.startsWith("\\") ? tok + " " : tok;
        }
        out += `_{${run.trimEnd()}}`;
        i = close + 1;
        continue;
      }
    }

    // Superscript run (²ⁿ⁺¹, ᵅᵝ) → ^{2n+1} / ^{\alpha\beta }
    if (Object.prototype.hasOwnProperty.call(SUP_REV, c)) {
      let run = "";
      while (i < src.length && Object.prototype.hasOwnProperty.call(SUP_REV, src[i])) {
        const tok = SUP_REV[src[i]];
        // If the mapping is a \command, append a space so it doesn't fuse with the next token.
        run += tok.startsWith("\\") ? tok + " " : tok;
        i++;
      }
      out += `^{${run.trimEnd()}}`;
      continue;
    }
    // Subscript run (ᵢⱼ, ᵦᵧ) → _{ij} / _{\beta\gamma }
    if (Object.prototype.hasOwnProperty.call(SUB_REV, c)) {
      let run = "";
      while (i < src.length && Object.prototype.hasOwnProperty.call(SUB_REV, src[i])) {
        const tok = SUB_REV[src[i]];
        run += tok.startsWith("\\") ? tok + " " : tok;
        i++;
      }
      out += `_{${run.trimEnd()}}`;
      continue;
    }

    // Greek glyph
    if (Object.prototype.hasOwnProperty.call(GREEK_TO_TEX, c)) {
      const cmd = GREEK_TO_TEX[c];
      // Add trailing space after a backslash command so it doesn't fuse with following letter
      out += cmd.startsWith("\\") ? cmd + " " : cmd;
      i++; continue;
    }

    // Operator / relation / arrow glyph
    if (Object.prototype.hasOwnProperty.call(SYM_TO_TEX, c)) {
      const cmd = SYM_TO_TEX[c];
      out += cmd.startsWith("\\") ? cmd + " " : cmd;
      i++; continue;
    }

    // Blackboard / calligraphic / fraktur — wrap consecutive run of same family
    const family =
      Object.prototype.hasOwnProperty.call(MATHBB_REV, c) ? "mathbb"
      : Object.prototype.hasOwnProperty.call(MATHCAL_REV, c) ? "mathcal"
      : Object.prototype.hasOwnProperty.call(MATHFRAK_REV, c) ? "mathfrak"
      : null;
    if (family) {
      const tbl = family === "mathbb" ? MATHBB_REV : family === "mathcal" ? MATHCAL_REV : MATHFRAK_REV;
      let run = "";
      while (i < src.length && Object.prototype.hasOwnProperty.call(tbl, src[i])) {
        run += tbl[src[i]];
        i++;
      }
      out += `\\${family}{${run}}`;
      continue;
    }

    // Combining accents — already overlay on previous char in Unicode; leave alone.
    // Plain pass-through
    out += c;
    i++;
  }

  return out;
}
